import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    TableRows, Insights, GridOn, Movie, PlayCircle,
    ToggleOn, ToggleOff, Analytics, ArrowOutward,
} from "@mui/icons-material";
import { Match } from "../../models/Match";
import { PlayerMatch } from "../../models/PlayerMatch";
import Loading from "../../components/Loading";
import ReplayViewer from "../MatchPage/Replay/ReplayViewer";
import { useReplayPosition, writeReplayParams } from "../MatchPage/Replay/replayLink";
import Match2Header from "../MatchPage2/Match2Header";
import Match2Scoreboard from "../MatchPage2/Match2Scoreboard";
import Match2Rounds from "../MatchPage2/Match2Rounds";
import Match2Detailed from "../MatchPage2/Match2Detailed";
import Match2Matrix from "../MatchPage2/Match2Matrix";
import { Side, compareRows, anyHasR3 } from "../MatchPage2/matchStats";
import match2 from "../MatchPage2/MatchPage2.module.css";
import {
    getUpload, getSharedUpload, getUploadMatch, getUploadPlayerMatches,
    fetchUploadReplayRound, checkUploadReplayAvailability, UploadedDemo,
} from "../../services/UserDemoService";
import ShareDemoPanel from "./ShareDemoPanel";
import ClipCreatorPanel from "./ClipCreatorPanel";
import styles from "./UserDemos.module.css";

type ViewMode = "overview" | "detailed" | "matrix" | "replay" | "clips";
const viewModes: ViewMode[] = ["overview", "detailed", "matrix", "replay", "clips"];

const UPLOAD_SEASON = 9999;

// The private viewer for a donator's own upload, served at /demo/:uploadId and
// /shared-demo/:token. It renders the same boards as the public match page (/match/:matchId)
// — Rating 3.0, Round Swing, eco-adjusted stats, the kill matrix — off the access-controlled
// /UserDemo endpoints instead of the public ones. An upload is a single .dem, so there is no
// map picker and no veto; clip creation replaces the public clips tab.
const DemoViewerPage: React.FC = () => {
    const { t } = useTranslation("pages");
    const { t: ts } = useTranslation("stats");
    const params = useParams<{ uploadId?: string; token?: string }>();
    const shareToken = params.token;
    const isShared = !!shareToken;

    const [demo, setDemo] = useState<UploadedDemo | null>(null);
    const [matches, setMatches] = useState<Match[] | null>(null);
    const [currentGame, setCurrentGame] = useState<Match | null>(null);
    const [players, setPlayers] = useState<PlayerMatch[]>([]);
    const [hasReplay, setHasReplay] = useState(false);
    const [side, setSide] = useState<Side>("all");
    const [eco, setEco] = useState(false);
    const [selectedSteamId, setSelectedSteamId] = useState<string | null>(null);
    // The tab and the replay round/moment live in the URL (?tab=replay&round=7&t=42) so a
    // share link can point at a moment, like the public match page.
    const [searchParams, setSearchParams] = useSearchParams();
    const [viewMode, setViewMode] = useState<ViewMode>(
        () => viewModes.find(m => m === searchParams.get("tab")) ?? "overview");
    const replayLink = useReplayPosition(searchParams);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        try {
            const d = isShared ? await getSharedUpload(shareToken!) : await getUpload(params.uploadId!);
            setDemo(d);
            if (d.status !== "completed") return;
            const uploadId = d.uploadId;
            const matchData = await getUploadMatch(uploadId, shareToken);
            setMatches(matchData);
            const game = matchData[0] ?? null;
            setCurrentGame(game);
            if (!game) return;

            // One request returns all ten rows; the boards split them by team below.
            setPlayers(await getUploadPlayerMatches(uploadId, game.gameid, shareToken));
            const availability = await checkUploadReplayAvailability(uploadId, game.gameid, shareToken);
            setHasReplay(availability.available);
        } catch (err: any) {
            setError(err?.response?.data?.error || t("userDemos.viewer.loadError"));
        }
    }, [isShared, shareToken, params.uploadId, t]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const next = new URLSearchParams(searchParams);
        if (viewMode === "overview") next.delete("tab"); else next.set("tab", viewMode);
        writeReplayParams(next, viewMode === "replay", replayLink.position);
        if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    }, [viewMode, searchParams, setSearchParams, replayLink.position]);

    const sortedTeam1 = useMemo(() => {
        const ids = currentGame?.team1playerids || [];
        return players.filter(p => ids.includes(p.steamId)).sort(compareRows(side));
    }, [players, currentGame, side]);

    const sortedTeam2 = useMemo(() => {
        const ids = currentGame?.team2playerids || [];
        return players.filter(p => ids.includes(p.steamId)).sort(compareRows(side));
    }, [players, currentGame, side]);

    // Detailed tab opens on the top-rated player still on the boards.
    useEffect(() => {
        if (viewMode !== "detailed") return;
        const all = [...sortedTeam1, ...sortedTeam2];
        if (all.length === 0) return;
        if (!selectedSteamId || !all.some(r => r.steamId === selectedSteamId)) {
            setSelectedSteamId(all[0].steamId);
        }
    }, [viewMode, sortedTeam1, sortedTeam2, selectedSteamId]);

    if (error) return <div className="container mt-4"><div className="alert alert-danger">{error}</div></div>;
    if (!demo) return <Loading />;

    if (demo.status === "processing")
        return <div className="container mt-4"><div className="alert alert-info">{t("userDemos.viewer.processing")}</div></div>;
    if (demo.status === "failed")
        return <div className="container mt-4"><div className="alert alert-danger">{t("userDemos.viewer.parseFailed", { error: demo.errorMessage })}</div></div>;
    if (!matches || !currentGame) return <Loading />;

    // Uploaded matches carry synthetic teams (season 9999 / "UserUpload"), so neither the
    // header meta line nor the team links would resolve to anything public.
    if (!currentGame.team1 || !currentGame.team2) {
        return (
            <div className="container mt-4">
                <div className="alert alert-danger">{t("userDemos.viewer.loadError")}</div>
            </div>
        );
    }

    const playerNames: Record<string, string> = {};
    players.forEach((p) => {
        if (p.account?.playerName) playerNames[p.steamId] = p.account.playerName;
    });

    const hasR3 = players.length === 0 || anyHasR3(players);

    // Analysis runs off the same access-controlled endpoints, so a share-link viewer can
    // open it too — the token rides along.
    const analyseParams = new URLSearchParams({ upload: demo.uploadId });
    if (shareToken) analyseParams.set("token", shareToken);
    const analyseHref = `/analyse?${analyseParams.toString()}`;

    const headerMeta = [
        t("userDemos.viewer.metaPrivate"),
        new Date(demo.createdAt).toLocaleDateString("da-DK").replaceAll(".", "/"),
    ].join(" · ");

    const tabDefs: { key: ViewMode; label: string; icon: React.ReactNode; show: boolean }[] = [
        { key: "overview", label: t("userDemos.viewer.tabOverview"), icon: <TableRows sx={{ fontSize: 19 }} />, show: true },
        { key: "detailed", label: t("userDemos.viewer.tabDetailed"), icon: <Insights sx={{ fontSize: 19 }} />, show: true },
        { key: "matrix", label: t("userDemos.viewer.tabMatrix"), icon: <GridOn sx={{ fontSize: 19 }} />, show: true },
        { key: "replay", label: t("userDemos.viewer.tabReplay"), icon: <PlayCircle sx={{ fontSize: 19 }} />, show: hasReplay },
        // Clip creation is owner-only — hide it for shared-link viewers.
        { key: "clips", label: t("userDemos.viewer.tabClips"), icon: <Movie sx={{ fontSize: 19 }} />, show: !isShared },
    ];
    const activeTab: ViewMode = tabDefs.some(d => d.key === viewMode && d.show) ? viewMode : "overview";

    const showSide = activeTab === "overview" || activeTab === "detailed" || activeTab === "matrix";
    const showEco = activeTab === "overview" && hasR3;

    const sideDefs: { key: Side; label: string; activeClass: string }[] = [
        { key: "all", label: t("match2.sideBoth"), activeClass: match2.sidePillBoth },
        { key: "t", label: "T", activeClass: match2.sidePillT },
        { key: "ct", label: "CT", activeClass: match2.sidePillCt },
    ];

    return (
        <div className={match2.page}>
            <div className={`${styles.banner} mb-3`}>
                {isShared ? t("userDemos.viewer.privateShared") : t("userDemos.viewer.private")}
            </div>

            {!isShared && <ShareDemoPanel uploadId={demo.uploadId} />}

            <Match2Header
                matches={matches}
                selectedMatch={currentGame}
                team1Score={currentGame.team1score}
                team2Score={currentGame.team2score}
                meta={headerMeta}
                linkTeams={false}
            />

            {/* Tabs */}
            <div className={match2.tabs}>
                {tabDefs.filter(d => d.show).map(d => (
                    <button
                        key={d.key}
                        className={`${match2.tab} ${activeTab === d.key ? match2.tabActive : ""}`}
                        onClick={() => setViewMode(d.key)}
                    >
                        {d.icon}
                        {d.label}
                    </button>
                ))}
                {hasReplay && (
                    <Link to={analyseHref} className={`${match2.tab} ${match2.tabLink}`} title={t("userDemos.viewer.analyseHint")}>
                        <Analytics sx={{ fontSize: 19 }} />
                        {t("userDemos.viewer.tabAnalyse")}
                        <ArrowOutward sx={{ fontSize: 13 }} className={match2.tabLinkArrow} />
                    </Link>
                )}
            </div>

            {/* Controls: eco toggle + side toggle (no map pills — an upload is a single map) */}
            {(showSide || showEco) && (
                <div className={match2.controls}>
                    <div />
                    <div className={match2.controlsRight}>
                        {showEco && (
                            <button
                                className={`${match2.ecoBtn} ${eco ? match2.ecoBtnActive : ""}`}
                                title={ts("ecoAdjustedTooltip")}
                                onClick={() => setEco(e => !e)}
                            >
                                {eco ? <ToggleOn sx={{ fontSize: 17 }} /> : <ToggleOff sx={{ fontSize: 17 }} />}
                                {ts("ecoAdjusted")}
                            </button>
                        )}
                        {showSide && (
                            <div className={match2.sideGroup}>
                                {sideDefs.map(sd => (
                                    <button
                                        key={sd.key}
                                        className={`${match2.sidePill} ${side === sd.key ? sd.activeClass : ""}`}
                                        onClick={() => setSide(sd.key)}
                                    >
                                        {sd.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ===== Overview ===== */}
            {activeTab === "overview" && (
                <div className={match2.fadeIn} style={{ marginTop: 18 }}>
                    <Match2Scoreboard
                        team={currentGame.team1}
                        score={currentGame.team1score}
                        rows={sortedTeam1}
                        side={side}
                        eco={eco && hasR3}
                        hasR3={hasR3}
                        linkTeam={false}
                    />
                    {currentGame.roundEnds && (
                        <Match2Rounds
                            roundEnds={currentGame.roundEnds}
                            team1={currentGame.team1}
                            team2Id={currentGame.team2?.teamId}
                            onRoundClick={hasReplay
                                ? (round) => { replayLink.openAt(round); setViewMode("replay"); }
                                : undefined}
                        />
                    )}
                    <Match2Scoreboard
                        team={currentGame.team2}
                        score={currentGame.team2score}
                        rows={sortedTeam2}
                        side={side}
                        eco={eco && hasR3}
                        hasR3={hasR3}
                        linkTeam={false}
                    />
                </div>
            )}

            {/* ===== Detailed ===== */}
            {activeTab === "detailed" && (
                <Match2Detailed
                    team1={currentGame.team1}
                    team2={currentGame.team2}
                    team1Rows={sortedTeam1}
                    team2Rows={sortedTeam2}
                    side={side}
                    selectedSteamId={selectedSteamId}
                    onSelect={setSelectedSteamId}
                />
            )}

            {/* ===== Kill matrix ===== */}
            {activeTab === "matrix" && (
                <Match2Matrix
                    team1={currentGame.team1}
                    team2={currentGame.team2}
                    team1Rows={sortedTeam1}
                    team2Rows={sortedTeam2}
                    side={side}
                />
            )}

            {/* ===== Replay ===== */}
            {activeTab === "replay" && (
                <div className={match2.replayWrapper} style={{ marginTop: 18 }}>
                    <ReplayViewer
                        season={UPLOAD_SEASON}
                        matchId={demo.matchId || `upload_${demo.uploadId}`}
                        gameId={currentGame.gameid}
                        mapName={currentGame.mapName}
                        playerNames={playerNames}
                        team1Name={currentGame.team1?.teamName}
                        team2Name={currentGame.team2?.teamName}
                        roundEnds={currentGame.roundEnds}
                        team1Id={currentGame.team1?.teamId}
                        team2Id={currentGame.team2?.teamId}
                        team1SteamIds={currentGame.team1playerids}
                        team1Logo={currentGame.team1?.teamLogo}
                        team2Logo={currentGame.team2?.teamLogo}
                        fetchRoundOverride={(_s, _m, g, r) => fetchUploadReplayRound(demo.uploadId, g, r, shareToken)}
                        checkAvailabilityOverride={(g) => checkUploadReplayAvailability(demo.uploadId, g, shareToken)}
                        initialRound={replayLink.position.round}
                        initialTime={replayLink.position.time}
                        onRoundChange={replayLink.onRoundChange}
                        // The owner's own /demo/:id URL opens for nobody else; sharing goes through the share panel.
                        canCopyLink={isShared}
                    />
                </div>
            )}

            {/* ===== Clips (owner-only) ===== */}
            {activeTab === "clips" && !isShared && (
                <ClipCreatorPanel uploadId={demo.uploadId} ownSteamId={demo.ownerSteamId} />
            )}
        </div>
    );
};

export default DemoViewerPage;
